import { initVersionedSchema } from '@principle-theorem/editor';
import {
  IDestinationEntityJobRunOptions,
  ITranslationMap,
  SourceEntityRecordStatus,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Brand,
  MedicareNumber,
  Note,
  Patient,
  ReferralSource,
  ServiceProviderHandler,
  stafferToNamedDoc,
} from '@principle-theorem/principle-core';
import {
  Gender,
  PatientStatus,
  type IContactNumber,
  type IDVACard,
  type IFeeSchedule,
  type IHealthFundCard,
  type IMedicareCard,
  type INote,
  type IPatient,
  type IPractice,
  type IReferralSource,
  type IReferralSourceConfiguration,
  type IStaffer,
  type ITag,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  ISO_DATE_FORMAT,
  WithPrimaryKey,
  getError,
  isSameRef,
  multiMap,
  snapshotCombineLatest,
  toNamedDocument,
  toTimestamp,
  type INamedDocument,
  type Timestamp,
  type WithRef,
} from '@principle-theorem/shared';
import * as he from 'he';
import { compact, first, sortBy, startCase } from 'lodash';
import * as moment from 'moment-timezone';
import { combineLatest, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DestinationEntity } from '../../../destination/destination-entity';
import {
  BasePatientDestinationEntity,
  IPatientJobData,
  IPatientMigrationData,
  PATIENT_RESOURCE_TYPE,
} from '../../../destination/entities/patient';
import { MedicareNumberCandidate } from '../../../destination/entities/lib/medicare-number-candidate';
import { PatientIdFilter } from '../../../destination/filters/patient-id-filter';
import { PracticeIdFilter } from '../../../destination/filters/practice-id-filter';
import { FEE_SCHEDULE_RESOURCE_TYPE } from '../../../mappings/fee-schedules';
import { ItemCodeNoteType } from '../../../mappings/item-codes-to-notes-xlsx';
import { PracticeMigration } from '../../../practice-migrations';
import { type TranslationMapHandler } from '../../../translation-map';
import {
  FeeScheduleSourceEntity,
  ID4WFeeSchedule,
} from '../../source/entities/fee-schedule';
import {
  D4WPatient,
  PatientSourceEntity,
  type ID4WPatient,
} from '../../source/entities/patient';
import {
  D4WDVACardType,
  PatientHealthFundSourceEntity,
  type ID4WPatientHealthFund,
} from '../../source/entities/patient-health-fund';
import {
  D4WPatientTreatmentNote,
  PatientTreatmentNoteSourceEntity,
} from '../../source/entities/patient-treatment-note';
import { D4WFeeScheduleToPatientTagMappingHandler } from '../mappings/fee-schedule-to-patient-tag';
import { D4WItemCodeToNoteMappingHandler } from '../mappings/item-code-to-note';
import {
  D4WPatientStatusMappingHandler,
  resolveD4WPatientStatus,
} from '../mappings/patient-statuses';
import { D4WPracticeMappingHandler } from '../mappings/practices';
import { D4WReferralSourceMappingHandler } from '../mappings/referral-sources';
import { D4WStafferMappingHandler } from '../mappings/staff';
import { ExistingPatientDestinationEntity } from './existing-patients';
import { FeeScheduleDestinationEntity } from './fee-schedules';
import { StafferDestinationEntity } from './staff';

export const PATIENT_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: PATIENT_RESOURCE_TYPE,
    label: 'Patients',
    description: '',
  },
});

/** D4W ends an entry that never expires in the year 3000. */
const noExpiryYear = 3000;
const unknownIrn = '?';

export interface ID4WPatientJobData extends IPatientJobData<D4WPatient> {
  sourcePatient: IGetRecordResponse<D4WPatient>;
  practices: WithPrimaryKey<ITranslationMap<IPractice>>[];
  sourceItemCodes: WithPrimaryKey<ITranslationMap<object, ItemCodeNoteType>>[];
  tags: INamedDocument<ITag>[];
  feeScheduleToPatientTags: WithPrimaryKey<ITranslationMap<ITag>>[];
  statuses: WithPrimaryKey<ITranslationMap<object, PatientStatus>>[];
  referralSources: WithPrimaryKey<
    ITranslationMap<IReferralSourceConfiguration>
  >[];
}

export class PatientDestinationEntity extends BasePatientDestinationEntity<D4WPatient> {
  destinationEntity = PATIENT_DESTINATION_ENTITY;
  patientSourceEntity = new PatientSourceEntity();

  override filters = [
    new PracticeIdFilter<ID4WPatientJobData>((jobData) =>
      jobData.sourcePatient.data.practice_id.toString()
    ),
    new PatientIdFilter<ID4WPatientJobData>((jobData) =>
      this.sourceEntities.patients
        .getSourceRecordId(jobData.sourcePatient.data)
        .toString()
    ),
  ];

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    patientHealthFunds: new PatientHealthFundSourceEntity(),
    treatmentNotes: new PatientTreatmentNoteSourceEntity(),
  };

  override destinationEntities = {
    feeSchedules: new FeeScheduleDestinationEntity(),
    existingPatients: new ExistingPatientDestinationEntity(),
    staff: new StafferDestinationEntity(),
  };

  customMappings = {
    itemCodes: new D4WItemCodeToNoteMappingHandler(),
    staff: new D4WStafferMappingHandler(),
    practices: new D4WPracticeMappingHandler(),
    feeScheduleToPatientTags: new D4WFeeScheduleToPatientTagMappingHandler(),
    statuses: new D4WPatientStatusMappingHandler(),
    referralSources: new D4WReferralSourceMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<ID4WPatientJobData[]> {
    const staff$ = this.customMappings.staff.getRecords(translationMap);
    const practices$ = this.customMappings.practices.getRecords(translationMap);
    const brand$ = PracticeMigration.brand$(migration);
    const sourceItemCodes$ =
      this.customMappings.itemCodes.getRecords(translationMap);
    const statuses$ = this.customMappings.statuses.getRecords(translationMap);
    const tags$ = brand$.pipe(
      switchMap((brand) => Brand.patientTags$(brand)),
      multiMap(toNamedDocument)
    );
    const feeScheduleToPatientTags$ =
      this.customMappings.feeScheduleToPatientTags.getRecords(translationMap);
    const referralSources$ =
      this.customMappings.referralSources.getRecords(translationMap);

    return snapshotCombineLatest([
      staff$,
      brand$,
      practices$,
      sourceItemCodes$,
      tags$,
      feeScheduleToPatientTags$,
      statuses$,
      referralSources$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          sourcePatients,
          [
            staff,
            brand,
            practices,
            sourceItemCodes,
            tags,
            feeScheduleToPatientTags,
            statuses,
            referralSources,
          ],
        ]) =>
          sourcePatients.map((sourcePatient) => ({
            sourcePatient,
            staff,
            brand,
            practices,
            sourceItemCodes,
            tags,
            feeScheduleToPatientTags,
            statuses,
            referralSources,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: ID4WPatientJobData
  ): Promise<
    | IPatientMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      if (
        data.sourcePatient.record.status === SourceEntityRecordStatus.Invalid
      ) {
        return this._buildErrorResponse(
          data.sourcePatient.record,
          'Source patient is invalid'
        );
      }

      const patient = data.sourcePatient.data;
      const translations = data.sourcePatient.record.translations;
      const sourcePatientId = this.sourceEntities.patients
        .getSourceRecordId(patient)
        .toString();

      const treatmentNotes =
        await this.sourceEntities.treatmentNotes.filterRecords(
          migration,
          'patientId',
          sourcePatientId
        );

      const healthFunds = (
        await this.sourceEntities.patientHealthFunds.filterRecords(
          migration,
          'patientId',
          sourcePatientId
        )
      ).map((healthFund) => healthFund.data);
      const medicareCard = getMedicareDetails(patient, healthFunds);

      const referrer = await getReferralSource(
        patient,
        data.referralSources,
        translationMap
      );

      return {
        sourcePatientId,
        createdAt: translations.first_presented_date ?? undefined,
        patient: Patient.init({
          name: getName(patient),
          email: patient.email ?? '',
          contactNumbers: getPhone(patient),
          address: getAddress(patient),
          dateOfBirth: patient.dob ?? undefined,
          gender: getGender(patient),
          status: resolveD4WPatientStatus(data.statuses, patient),
          notes: compact([
            buildPatientCommentNote(patient),
            getSecondaryHealthFundNote(healthFunds, medicareCard),
            ...buildSocialNotesFromTreatments(treatmentNotes, data),
          ]),
          tags: this._getPatientTags(data, patient),
          referenceId: sourcePatientId,
          medicareCard,
          healthFundCard: getHealthFundDetails(healthFunds),
          dvaCard: getDVADetails(healthFunds),
          referrer,
          preferredPractice: patient.practice_id
            ? await getPreferredPractice(
                patient.practice_id.toString(),
                data.practices
              )
            : undefined,
          preferredDentist: patient.provider_id
            ? await getPreferredProvider(
                patient.provider_id.toString(),
                data.staff
              )
            : undefined,
          preferredFeeSchedule: patient.practice_fee_level_id
            ? await getDefaultFeeSchedule(
                {
                  id: patient.practice_fee_level_id,
                  practice_id: patient.practice_id ? patient.practice_id : 0,
                },
                translationMap
              )
            : undefined,
        }),
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }

  private _getPatientTags(
    data: ID4WPatientJobData,
    patient: ID4WPatient
  ): INamedDocument<ITag>[] {
    const preferredFeeSchedule = patient.practice_fee_level_id?.toString();
    if (!preferredFeeSchedule) {
      return [];
    }

    const tagMapping = data.feeScheduleToPatientTags.find(
      (mapping) => mapping.sourceIdentifier === preferredFeeSchedule
    );

    if (!tagMapping?.destinationIdentifier) {
      return [];
    }

    const foundTag = data.tags.find((tag) =>
      isSameRef(tag, tagMapping.destinationIdentifier)
    );

    if (!foundTag) {
      return [];
    }

    return [foundTag];
  }
}

function buildPatientCommentNote(patient: ID4WPatient): INote | undefined {
  const patientComments = patient.marketing_details.trim();
  if (!patientComments) {
    return;
  }

  return Note.init({
    content: initVersionedSchema(patient.marketing_details.trim()),
  });
}

export function getMedicareDetails(
  patient: Pick<ID4WPatient, 'medicare_id'>,
  healthFunds: ID4WPatientHealthFund[]
): IMedicareCard | undefined {
  const medicareNumber = MedicareNumberCandidate.choose([
    { value: patient.medicare_id ?? undefined },
    ...defaultFirst(healthFunds).map((healthFund) => ({
      value: healthFund.membership_number,
      irn: healthFund.member_number ?? undefined,
      expiryDate: getExpiryDate(healthFund.expiry_date),
    })),
  ]);

  if (medicareNumber) {
    return {
      number: medicareNumber.number,
      subNumerate: medicareNumber.irn ?? unknownIrn,
      expiryDate: medicareNumber.expiryDate,
    };
  }

  if (!patient.medicare_id) {
    return;
  }

  return {
    number: patient.medicare_id,
    subNumerate: unknownIrn,
  };
}

function getExpiryDate(expiryDate: string): Timestamp | undefined {
  const expiry = moment.utc(expiryDate, ISO_DATE_FORMAT);
  return expiry.isValid() && expiry.year() < noExpiryYear
    ? toTimestamp(expiry)
    : undefined;
}

function defaultFirst(
  healthFunds: ID4WPatientHealthFund[]
): ID4WPatientHealthFund[] {
  return sortBy(healthFunds, (healthFund) => !healthFund.is_default);
}

function getPrimaryHealthFund(
  healthFunds: ID4WPatientHealthFund[]
): ID4WPatientHealthFund | undefined {
  return first(
    defaultFirst(healthFunds).filter(
      (healthFund) =>
        !healthFund.is_dva &&
        !MedicareNumber.isValid(healthFund.membership_number)
    )
  );
}

function getDvaFund(
  healthFunds: ID4WPatientHealthFund[]
): ID4WPatientHealthFund | undefined {
  return first(healthFunds.filter((healthFund) => healthFund.is_dva));
}

export function getHealthFundDetails(
  healthFunds: ID4WPatientHealthFund[]
): IHealthFundCard | undefined {
  const patientHealthFund = getPrimaryHealthFund(healthFunds);
  if (!patientHealthFund) {
    return;
  }

  return {
    membershipNumber: patientHealthFund.membership_number,
    memberNumber: patientHealthFund.member_number
      ? patientHealthFund.member_number.toString()
      : '',
    fundCode: patientHealthFund.fund_code,
  };
}

function getDVADetails(
  healthFunds: ID4WPatientHealthFund[]
): IDVACard | undefined {
  const dvaFundDetails = getDvaFund(healthFunds);
  if (!dvaFundDetails || !dvaFundDetails.membership_number) {
    return;
  }

  return {
    number: dvaFundDetails.membership_number,
    expiryDate: toTimestamp(moment(dvaFundDetails.expiry_date)),
  };
}

export function getSecondaryHealthFundNote(
  healthFunds: ID4WPatientHealthFund[],
  medicareCard?: IMedicareCard
): INote | undefined {
  const primaryHealthFund = getPrimaryHealthFund(healthFunds);
  const dvaFund = getDvaFund(healthFunds);
  const primaryFundIds = compact([
    primaryHealthFund?.fund_id,
    dvaFund?.fund_id,
  ]);

  const secondaryFunds = healthFunds.filter(
    (healthFund) =>
      !primaryFundIds.includes(healthFund.fund_id) &&
      // Another Medicare card stays in the note, so its number is not lost.
      !MedicareNumber.isSameCard(
        healthFund.membership_number,
        medicareCard?.number
      )
  );
  if (!secondaryFunds.length) {
    return;
  }

  const lines = [
    'Additional insurance cards (migrated from D4W):',
    ...secondaryFunds.map(formatSecondaryHealthFundLine),
  ];

  return Note.init({
    pinned: true,
    content: initVersionedSchema(lines.join('\n')),
  });
}

function formatSecondaryHealthFundLine(
  healthFund: ID4WPatientHealthFund
): string {
  const memberNumber = healthFund.member_number?.toString().trim();
  const expiryDate = healthFund.expiry_date?.trim();
  const dvaSuffix =
    healthFund.is_dva && healthFund.dva_card_type
      ? ` [DVA ${D4WDVACardType[healthFund.dva_card_type]}]`
      : '';

  return (
    `${healthFund.fund_name} (${healthFund.fund_code}) — ` +
    `Membership: ${healthFund.membership_number || '-'}, ` +
    `Member: ${memberNumber || '-'}, ` +
    `Expiry: ${expiryDate || '-'}${dvaSuffix}`
  );
}

export function getGender(patient: ID4WPatient): Gender {
  switch (patient.patient_sex) {
    case 'F':
      return Gender.Female;
    case 'M':
      return Gender.Male;
    default:
      return Gender.NotSpecified;
  }
}

export function getAddress(patient: ID4WPatient): string {
  const addressFields = compact([
    patient.address_1?.trim(),
    startCase(patient.suburb?.trim().toLowerCase()),
    patient.state?.trim(),
    patient.postcode?.toString().trim(),
  ]);

  if (!addressFields.length) {
    return '';
  }

  return addressFields.join(', ');
}

export function getPhone(patient: ID4WPatient): IContactNumber[] {
  const d4wContactNumbers = compact([
    patient.mobile ? { label: 'mobile', number: patient.mobile } : undefined,
    patient.phone_home
      ? { label: 'home', number: patient.phone_home }
      : undefined,
    patient.phone_work
      ? { label: 'work', number: patient.phone_work }
      : undefined,
  ]);
  return d4wContactNumbers.filter((contactNumbers) => contactNumbers.number);
}

export function getName(patient: ID4WPatient): string {
  const preferredName = patient.preferred_name?.trim() ?? '';

  if (!preferredName || preferredName === patient.firstname) {
    return he.decode(`${patient.firstname} ${patient.surname}`.trim());
  }
  return he.decode(
    `${patient.firstname} (${preferredName}) ${patient.surname}`.trim()
  );
}

async function getPreferredProvider(
  preferredProviderId: string,
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[]
): Promise<INamedDocument<IStaffer> | undefined> {
  const practitionerMap = staff.find(
    (staffer) => staffer.sourceIdentifier === preferredProviderId
  );

  if (!practitionerMap?.destinationIdentifier) {
    return;
  }

  return stafferToNamedDoc(
    await Firestore.getDoc(practitionerMap.destinationIdentifier)
  );
}

async function getPreferredPractice(
  preferredPracticeId: string,
  practices: WithPrimaryKey<ITranslationMap<IPractice>>[]
): Promise<INamedDocument<IPractice> | undefined> {
  const practitionerMap = practices.find(
    (practice) => practice.sourceIdentifier === preferredPracticeId
  );

  if (!practitionerMap?.destinationIdentifier) {
    return;
  }

  return toNamedDocument(
    await Firestore.getDoc(practitionerMap.destinationIdentifier)
  );
}

async function getDefaultFeeSchedule(
  data: Pick<ID4WFeeSchedule, 'id' | 'practice_id'>,
  translationMap: TranslationMapHandler
): Promise<INamedDocument<IFeeSchedule> | undefined> {
  const entity = new FeeScheduleSourceEntity();
  const feeScheduleMap = await translationMap.getDestination<IFeeSchedule>(
    entity.getSourceRecordId(data).toString(),
    FEE_SCHEDULE_RESOURCE_TYPE
  );

  if (!feeScheduleMap) {
    return;
  }

  return toNamedDocument(await Firestore.getDoc(feeScheduleMap));
}

function buildSocialNotesFromTreatments(
  notes: IGetRecordResponse<D4WPatientTreatmentNote>[],
  data: ID4WPatientJobData
): INote[] {
  return compact(
    notes.map((note) => {
      const procedureCode = note.data.item_code;
      const code = ServiceProviderHandler.findServiceCode(procedureCode);

      if (code) {
        return;
      }

      const mappedCode = data.sourceItemCodes.find(
        (sourceItemCode) =>
          sourceItemCode.sourceIdentifier === procedureCode &&
          !!sourceItemCode.destinationValue
      );

      if (
        !mappedCode ||
        mappedCode.destinationValue?.value !==
          ItemCodeNoteType.PatientSocialNote
      ) {
        return;
      }

      return Note.init({
        content: initVersionedSchema(note.data.content),
        createdAt: note.record.translations.createdAt,
      });
    })
  );
}

async function getReferralSource(
  patient: ID4WPatient,
  referralSources: WithPrimaryKey<
    ITranslationMap<IReferralSourceConfiguration>
  >[],
  translationMap: TranslationMapHandler
): Promise<IReferralSource | undefined> {
  if (!patient.source_code) {
    return;
  }

  if (patient.patient_referrer_id) {
    const patientRef = await translationMap.getDestination<IPatient>(
      patient.patient_referrer_id.toString(),
      PATIENT_RESOURCE_TYPE
    );

    if (!patientRef) {
      return;
    }

    const referralPatient = await Firestore.getDoc(patientRef);
    return ReferralSource.toReferrer(referralPatient);
  }

  if (patient.refsource_id) {
    const referralSourceMap = referralSources.find(
      (referralSource) =>
        referralSource.sourceIdentifier === patient.refsource_id?.toString()
    );
    if (!referralSourceMap?.destinationIdentifier) {
      return;
    }

    const referralSource = await Firestore.getDoc(
      referralSourceMap.destinationIdentifier
    );
    return ReferralSource.toReferrer(referralSource);
  }
}
